import closeWindowAtom from "./uiState/closeWindowAtom";
import activeWindowAtom from "app/uiState/activeWindowAtom";
import { WindowContext } from "app/WindowStateProvider";
import getWindowAtom from "./uiState/getWindowAtom";
import { useContext, useCallback } from "react";
import { useAtomValue, useAtom, atom } from "jotai";
import { useAtomCallback } from "jotai/utils";
import { useIsMobileOrTablet } from "app/jm";
import type {
  UIProps,
  ComponentState,
  ComponentName,
} from "./componentOptions";
import componentStackAtom from "./uiState/mobile/componentStackAtom";
import componentStackIdsAtom from "./uiState/mobile/ComponentStackIdsAtom";
import { MobileComponentStateContext } from "./uiState/mobile/MobileComponentStateContext";
import { useComponentCloseContext } from "app/ComponentCloseProvider";
import { CurrentRoutedComponentContext } from "./router/currentRoutedComponent";

// This only exists because we cannot conditionally call useAtom
// if the windowId is not found in the context.
const defaultComponentAtom = atom<ComponentState<ComponentName> | null>(null);

export function useCurrentComponent() {
  const isMobileOrTablet = useIsMobileOrTablet();
  const closeContext = useComponentCloseContext();

  const windowContextValue = useContext(WindowContext);
  const mobileComponentAtom = useContext(MobileComponentStateContext);
  const routedComponentContext = useContext(CurrentRoutedComponentContext);

  const windowId = windowContextValue?.windowId || null;

  const [windowComponentState, setWindowComponentState] = useAtom(
    windowId ? getWindowAtom(windowId) : defaultComponentAtom,
  );

  const mobileComponentStack = useAtomValue(componentStackAtom);

  const mobileStackEntryAtom =
    mobileComponentAtom ??
    (mobileComponentStack.length > 0
      ? mobileComponentStack.at(-1)!.componentAtom
      : defaultComponentAtom);

  const [mobileComponentState, setMobileComponentState] =
    useAtom(mobileStackEntryAtom);

  const componentState = isMobileOrTablet
    ? (routedComponentContext?.componentState ?? mobileComponentState)
    : windowComponentState;

  const setUIProp = useCallback(
    <K extends keyof UIProps>(prop: K, value: UIProps[K]) => {
      if (isMobileOrTablet && routedComponentContext) {
        return routedComponentContext.setComponentState((prevState) => ({
          ...prevState,
          uiProps: {
            ...prevState.uiProps,
            [prop]: value,
          },
        }));
      }

      const setter = isMobileOrTablet
        ? setMobileComponentState
        : setWindowComponentState;

      return setter((prevState) => {
        if (prevState === null) {
          throw new Error("Component not found");
        }

        return {
          ...prevState,
          uiProps: {
            ...prevState.uiProps,
            [prop]: value,
          },
        };
      });
    },
    [
      isMobileOrTablet,
      routedComponentContext,
      setMobileComponentState,
      setWindowComponentState,
    ],
  );

  const updateUIProps = useCallback(
    (newUIProps: Partial<UIProps>) => {
      if (isMobileOrTablet && routedComponentContext) {
        return routedComponentContext.setComponentState((prevState) => ({
          ...prevState,
          uiProps: {
            ...prevState.uiProps,
            ...newUIProps,
          },
        }));
      }

      const setter = isMobileOrTablet
        ? setMobileComponentState
        : setWindowComponentState;

      return setter((prevState) => {
        if (prevState === null) {
          throw new Error("Component not found");
        }

        return {
          ...prevState,
          uiProps: {
            ...prevState.uiProps,
            ...newUIProps,
          },
        };
      });
    },
    [
      isMobileOrTablet,
      routedComponentContext,
      setMobileComponentState,
      setWindowComponentState,
    ],
  );

  const updateComponentState = useCallback(
    (
      updater: (
        state: ComponentState<ComponentName>,
      ) => ComponentState<ComponentName>,
    ) => {
      if (isMobileOrTablet && routedComponentContext) {
        return routedComponentContext.setComponentState((prevState) =>
          updater(prevState),
        );
      }

      const setter = isMobileOrTablet
        ? setMobileComponentState
        : setWindowComponentState;

      return setter((prevState) => {
        if (prevState === null) {
          throw new Error("Component not found");
        }

        return updater(prevState);
      });
    },
    [
      isMobileOrTablet,
      routedComponentContext,
      setMobileComponentState,
      setWindowComponentState,
    ],
  );

  const closeImmediately = useAtomCallback(
    useCallback(
      (get, set) => {
        if (isMobileOrTablet) {
          if (routedComponentContext) {
            routedComponentContext.closeImmediately();
            return;
          }

          const currentStack = get(componentStackAtom);

          if (currentStack.length > 0) {
            const updatedStack = mobileComponentAtom
              ? currentStack.filter(
                  (entry) => entry.componentAtom !== mobileComponentAtom,
                )
              : currentStack.slice(0, -1);

            set(componentStackAtom, updatedStack);
            set(
              componentStackIdsAtom,
              updatedStack.map((entry) => get(entry.componentAtom).componentId),
            );
          }

          return;
        }

        if (windowId) {
          set(closeWindowAtom, windowId);
        }
      },
      [isMobileOrTablet, mobileComponentAtom, routedComponentContext, windowId],
    ),
  );

  /**
   * Request close if a ComponentCloseProvider exists.
   * Otherwise close immediately.
   */
  const close = useCallback(() => {
    if (closeContext) {
      closeContext.requestClose();
      return;
    }

    closeImmediately();
  }, [closeContext, closeImmediately]);

  const setAsActive = useAtomCallback(
    useCallback(
      (_get, set) => {
        set(activeWindowAtom, windowId);
      },
      [windowId],
    ),
  );

  if (!isMobileOrTablet && windowContextValue === null) {
    throw new Error("WindowContext not found");
  }

  if (!componentState) {
    return null;
  }

  return {
    windowId,
    componentState,
    setUIProp,
    updateUIProps,
    updateComponentState,
    close,
    closeImmediately,
    setAsActive,
  } as const;
}
